/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo } from "react";
import { CheckCircle2, Calendar, Video, Mail, ArrowRight } from "lucide-react";

const RED = "#E32126";

function nextSunday11AMIST() {
  const now = new Date();
  const istOffsetMin = 330;
  const nowUtcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const nowIst = new Date(nowUtcMs + istOffsetMin * 60_000);
  const dayIst = nowIst.getUTCDay();
  const daysAhead = (7 - dayIst) % 7;
  const target = new Date(Date.UTC(nowIst.getUTCFullYear(), nowIst.getUTCMonth(), nowIst.getUTCDate() + daysAhead, 11, 0, 0, 0));
  const targetUtcMs = target.getTime() - istOffsetMin * 60_000;
  if (targetUtcMs <= now.getTime()) return new Date(targetUtcMs + 7 * 24 * 60 * 60_000);
  return new Date(targetUtcMs);
}

function formatIst(d) {
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function WebinarConfirmedPage() {
  const nextSession = useMemo(() => nextSunday11AMIST(), []);

  useEffect(() => {
    document.title = "Seat Confirmed · Digital Sales Engine — BrandChilly";
    try {
      if (window.fbq) window.fbq("track", "CompleteRegistration", { content_name: "Webinar" });
    } catch (e) { /* swallow */ }
  }, []);

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" />

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-black/10">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
          <a href="/" className="text-lg md:text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            BRAND<span style={{ color: RED }}>CHILLY</span>
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="webinar-confirmed-page">
        <div className="flex items-center gap-3" style={{ color: RED }}>
          <CheckCircle2 size={24} />
          <p className="text-[11px] tracking-[0.24em] uppercase font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            Seat confirmed
          </p>
        </div>

        <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05]" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
          You're in. See you <span style={{ color: RED }}>Sunday</span>.
        </h1>

        <p className="mt-5 text-lg text-black/70 leading-relaxed">
          Your ₹199 payment has cleared and your seat for the Digital Sales Engine workshop is locked. A confirmation email with the join link and calendar (.ics) invite is on its way to your inbox.
        </p>

        <div className="mt-8 border border-black/15 p-6 md:p-7" style={{ borderRadius: "3px" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-black/50 mb-1.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Next Session</div>
              <div className="flex items-start gap-2.5">
                <Calendar size={18} className="mt-1 shrink-0" style={{ color: RED }} />
                <div className="text-[15px] font-semibold leading-snug">{formatIst(nextSession)}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-black/50 mb-1.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Format</div>
              <div className="flex items-start gap-2.5">
                <Video size={18} className="mt-1 shrink-0" style={{ color: RED }} />
                <div className="text-[15px] font-semibold leading-snug">Live online — 3-hour workshop + Q&A</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          <div className="flex gap-3">
            <div className="w-6 h-6 shrink-0 flex items-center justify-center text-white font-bold text-xs" style={{ background: RED, borderRadius: "3px" }}>1</div>
            <div>
              <p className="font-semibold">Check your inbox.</p>
              <p className="text-sm text-black/70 mt-0.5">Your confirmation email (with join link + calendar invite) will land within 2 minutes. Check spam if you don't see it.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-6 h-6 shrink-0 flex items-center justify-center text-white font-bold text-xs" style={{ background: RED, borderRadius: "3px" }}>2</div>
            <div>
              <p className="font-semibold">Add it to your calendar.</p>
              <p className="text-sm text-black/70 mt-0.5">Open the attached <code className="text-xs bg-black/5 px-1.5 py-0.5">.ics</code> file — it drops the session straight into Google, Apple or Outlook calendar.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-6 h-6 shrink-0 flex items-center justify-center text-white font-bold text-xs" style={{ background: RED, borderRadius: "3px" }}>3</div>
            <div>
              <p className="font-semibold">Come prepared.</p>
              <p className="text-sm text-black/70 mt-0.5">Have your current niche and offer in mind — you'll build a positioning statement and funnel map in the room.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-black/10 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-sm text-black/70">
            <Mail size={16} style={{ color: RED }} />
            Need help? <a href="mailto:brandchilly@gmail.com" className="font-semibold text-black hover:opacity-80" style={{ color: RED }}>brandchilly@gmail.com</a>
          </div>
          <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold text-black hover:opacity-70" data-testid="webinar-confirmed-home">
            Back to BrandChilly <ArrowRight size={16} />
          </a>
        </div>
      </main>
    </div>
  );
}
